import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/auth.service';
import './OAuthCallback.css';

const OAuthCallback: React.FC = () => {
  const { provider } = useParams<{ provider: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let hasProcessed = false;

    const handleCallback = async () => {
      // Prevent duplicate processing
      if (hasProcessed) {
        return;
      }
      hasProcessed = true;

      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        if (error) {
          throw new Error(errorDescription || `OAuth error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received');
        }

        if (!state) {
          throw new Error('No state parameter received');
        }

        if (!provider) {
          throw new Error('Invalid provider');
        }

        // Only process if component is still mounted
        if (!isMounted) return;

        // Handle the OAuth callback
        await authService.handleOAuthCallback(provider, code, state);
        
        // Auth service will redirect on success, so this won't be reached
      } catch (err) {
        console.error('OAuth callback error:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Authentication failed');
          setLoading(false);
          
          // Redirect to home after 3 seconds
          setTimeout(() => {
            if (isMounted) {
              navigate('/');
            }
          }, 3000);
        }
      }
    };

    handleCallback();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [provider, location, navigate]);

  return (
    <div className="oauth-callback-container">
      <div className="oauth-callback-content">
        {loading ? (
          <>
            <div className="oauth-spinner"></div>
            <h2>Completing sign in...</h2>
            <p>Please wait while we authenticate your account.</p>
          </>
        ) : error ? (
          <>
            <div className="oauth-error-icon">⚠️</div>
            <h2>Authentication Failed</h2>
            <p>{error}</p>
            <p className="redirect-message">Redirecting to home page...</p>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default OAuthCallback;